<script lang="ts" setup>
import { ref } from "vue";
import { Exchange, FeedbackType } from "./types/Exchange";
import Waiting from "./Waiting.vue";
import { useProtocolStore } from "./store/protocol-store";
import { BSpinner } from "bootstrap-vue";

const props = defineProps<{
  exchange: Exchange;
}>();

const feedbackPositive = ref(true);

const protocolStore = useProtocolStore();

const providePositiveFeedback = async () => {
  if (props.exchange.feedbackType || !props.exchange.questionUuid) {
    return;
  }
  await protocolStore.submitFeedback(props.exchange.questionUuid, true);
};

const provideNegativeFeedback = () => {
  if (props.exchange.feedbackType) {
    return;
  }
  feedbackPositive.value = false;
};

const cancelNegativeFeedback = () => (feedbackPositive.value = true);

const feedbackFieldName = "feedback";

const thumbsUpColor = () => {
  if (props.exchange.feedbackType === FeedbackType.Positive) {
    return "success";
  }
  return "";
};

const thumbsDownColor = () => {
  if (props.exchange.feedbackType === FeedbackType.Negative) {
    return "danger";
  }
  return "";
};

const getIcon = (icon: string, feedbackType: FeedbackType) => {
  if (props.exchange.feedbackType === feedbackType) {
    return `${icon}-fill`;
  }
  return icon;
};

const submit = async (event) => {
  const formData = new FormData(event.target);

  const feedbackValue = formData.get(feedbackFieldName);

  if (typeof feedbackValue === "string" && props.exchange.questionUuid) {
    await protocolStore.submitFeedback(props.exchange.questionUuid, false, feedbackValue);
    feedbackPositive.value = true;
    event.target.reset();
  }
};
</script>

<template>
  <div>
    <div class="feedback">
      <div v-if="feedbackPositive" class="thumbs">
        <Waiting v-if="protocolStore.sendingFeedback.value" />
        <span v-else>
          <b-icon
            v-if="!(props.exchange.feedbackType === FeedbackType.Negative)"
            :icon="getIcon('hand-thumbs-up', FeedbackType.Positive)"
            class="m-2"
            :variant="thumbsUpColor()"
            @click="providePositiveFeedback"
          ></b-icon>
          <b-icon
            v-if="!(props.exchange.feedbackType === FeedbackType.Positive)"
            :icon="getIcon('hand-thumbs-down', FeedbackType.Negative)"
            class="m-2"
            :variant="thumbsDownColor()"
            @click="provideNegativeFeedback"
          ></b-icon>
        </span>
      </div>
    </div>
    <!-- Negative Feedback Section & Overlay -->
    <div v-if="!feedbackPositive">
      <!-- Overlay -->
      <div class="feedback-overlay"></div>
      <!-- Form -->
      <div class="negativeFeedback">
        <form @submit.prevent="submit">
          <fieldset>
            <div class="mb-3">
              <div class="negativeFeedbackHeader">
                <label class="form-label" for="feedback"><b>Feedback</b></label>
                <button class="dialog-close" @click="cancelNegativeFeedback">
                  <svg class="svg-icon" viewBox="0 0 20 20">
                    <path
                      d="M15.898,4.045c-0.271-0.272-0.713-0.272-0.986,0l-4.71,4.711L5.493,4.045c-0.272-0.272-0.714-0.272-0.986,0s-0.272,0.714,0,0.986l4.709,4.711l-4.71,4.711c-0.272,0.271-0.272,0.713,0,0.986c0.136,0.136,0.314,0.203,0.492,0.203c0.179,0,0.357-0.067,0.493-0.203l4.711-4.711l4.71,4.711c0.137,0.136,0.314,0.203,0.494,0.203c0.178,0,0.355-0.067,0.492-0.203c0.273-0.273,0.273-0.715,0-0.986l-4.711-4.711l4.711-4.711C16.172,4.759,16.172,4.317,15.898,4.045z"
                      fill="currentcolor"
                    ></path>
                  </svg>
                </button>
              </div>

              <input id="feedback" class="form-control" name="feedback" required type="search" />
            </div>
            <div class="feedbackButtons">
              <b-button
                size="sm"
                variant="outline-primary"
                name="button"
                type="submit"
                :disabled="protocolStore.sendingFeedback.value"
              >
                <BSpinner v-if="protocolStore.sendingFeedback.value" small />
                <span v-else>Submit</span>
              </b-button>
            </div>
          </fieldset>
        </form>
      </div>
    </div>
  </div>
</template>

<style scoped>
.feedback {
  display: flex;
  justify-content: flex-end;
  position: relative; /* Keep for thumbs positioning if needed, doesn't harm */
}

.feedback-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5); /* Semi-transparent black */
  z-index: 99; /* Below the form, above other content */
}

.negativeFeedback {
  display: block;
  padding: 1rem;
  border: 1px grey solid;
  border-radius: 0.3rem;
  /* Change to fixed positioning */
  position: fixed;
  /* Center on screen */
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  /* Ensure it's above the overlay */
  z-index: 100;
  background-color: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  min-width: 300px; /* Adjusted min-width */
}

.negativeFeedbackHeader {
  display: flex;
  justify-content: space-between;
}

.feedbackButtons {
  display: flex;
  justify-content: space-around;
}

.thumbs {
  padding-inline: 1rem;
}

.dialog-close {
  background-color: transparent;
  border-radius: 50%;
  border: 0;
  cursor: pointer;
  font-size: 1.5em;
  height: 1.5em;
  padding: 0.25em;
  text-align: center;
  transition: 0.15s;
  width: 1.5em;
}

.dialog-close .svg-icon {
  aspect-ratio: 1;
  bottom: 8px;
  position: relative;
  width: 20px;
}

.dialog-close:hover {
  background-color: rgb(50 50 0 / 0.15);
}
</style>
